import { EntityHistoryActions, type EntityHistoryEntry, type EntityHistoryFieldChange } from '@/types'

const SYSTEM_ACTOR_ID = 'SystemBackgroundWorker'
const SUMMARY_VALUE_MAX_LENGTH = 60

export type EntityHistoryFieldLabels = Record<string, string>

export function actorName(entry: EntityHistoryEntry): string {
  if (entry.performedByDisplayName) return entry.performedByDisplayName
  if (entry.performedByEmail) return entry.performedByEmail
  if (entry.performedByUserId === SYSTEM_ACTOR_ID) return 'System'

  return 'Unknown user'
}

export function fieldLabel(fieldName: string, labels: EntityHistoryFieldLabels): string {
  return labels[fieldName] ?? fieldName.replace(/([A-Z])/g, ' $1').trim()
}

export function displayOldValue(change: EntityHistoryFieldChange): string {
  return formatValue(preferLabel(change.oldValueLabel, change.oldValue))
}

export function displayNewValue(change: EntityHistoryFieldChange): string {
  return formatValue(preferLabel(change.newValueLabel, change.newValue))
}

// A blank label is not a label: a user invited but never signed in resolves to an empty name, and
// `??` would keep it, rendering a real assignment as if the field had been cleared.
function preferLabel(label: string | null, value: string | null): string | null {
  return label?.trim() ? label : value
}

function formatValue(value: string | null): string {
  return value?.trim() ? value : '(empty)'
}

export function describeEntry(
  entry: EntityHistoryEntry,
  entityLabel: string,
  labels: EntityHistoryFieldLabels,
): string {
  if (entry.action === EntityHistoryActions.Created) return `created the ${entityLabel}`
  if (entry.action === EntityHistoryActions.Deleted) return `deleted the ${entityLabel}`

  if (entry.changes.length === 0) return `updated the ${entityLabel}`

  if (entry.changes.length === 1) {
    const change = entry.changes[0]
    const label = fieldLabel(change.fieldName, labels)
    const newValue = preferLabel(change.newValueLabel, change.newValue)

    return newValue?.trim()
      ? `changed ${label} to ${summarize(newValue)}`
      : `cleared ${label}`
  }

  return `changed ${joinWithAnd(entry.changes.map(change => fieldLabel(change.fieldName, labels)))}`
}

function summarize(value: string): string {
  const collapsed = value.replace(/\s+/g, ' ').trim()

  return collapsed.length > SUMMARY_VALUE_MAX_LENGTH
    ? `${collapsed.slice(0, SUMMARY_VALUE_MAX_LENGTH)}…`
    : collapsed
}

function joinWithAnd(labels: string[]): string {
  if (labels.length <= 1) return labels[0] ?? ''

  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
}
